import { useMemo } from 'react';
import { tradeDateInLastNDays } from '../services/timeService';
import { buildAccountTypeIndex } from '../services/accountTypeClassifier';

/**
 * Custom hook for the trade filtering pipeline.
 * Extracted from TradingJournal.js (Phase 5).
 *
 * Pipeline: trades → filteredTrades (account/type/active/reset/time filters)
 *         → closedFilteredTrades (remove open trades)
 *         → cleanClosedTrades (remove mistake-tagged, memoized)
 *         → setupAtmBaseTrades (conditional on excludeMistakesFromSetupStats)
 *
 * `selectedAccountTypes` is an array of category strings — when non-empty,
 * trades are filtered to accounts whose classified type is in the set.
 * Empty array (default) means no type-filter is applied. Composes with
 * `selectedAccountFilter` — if both are set, both must pass.
 */
const useTradeFiltering = ({
  trades,
  accounts,
  selectedAccountFilter,
  selectedAccountTypes = [],
  activeAccountsOnly,
  includePassedInStats,
  includeResetTradesInStats,
  timePeriodFilter,
  excludeMistakesFromSetupStats,
  getAccountById,
}) => {

  // Get list of active account IDs for filtering (include Passed if toggle is on)
  const activeAccountIds = accounts
    .filter(a => a.status === 'Active' || (includePassedInStats && a.status === 'Passed'))
    .map(a => a.id);

  // Account-type lookup — memoized against the accounts list. Used by the
  // selectedAccountTypes filter below. Cheap to recompute (O(n)) but the
  // memo keeps the Map identity stable across renders.
  const accountTypeByIdMemo = useMemo(() => buildAccountTypeIndex(accounts), [accounts]);

  // Filter trades based on selected account, account-type filter,
  // active-only filter, time period, and reset period.
  const filteredTrades = (() => {
    let result = trades;

    // First filter by selected account if not "all"
    if (selectedAccountFilter !== 'all') {
      result = result.filter(t => t.accountId === selectedAccountFilter);
    }

    // Account-type filter (P&L by Sim / Eval / Funded / Live). Only applies
    // when "all" accounts is selected — explicit single-account selection
    // bypasses the type filter (the user already picked a specific bucket).
    // Trades with no `accountId` (manual entries, legacy imports) are
    // passed through unchanged so they don't silently disappear.
    if (selectedAccountFilter === 'all' && Array.isArray(selectedAccountTypes) && selectedAccountTypes.length > 0) {
      const allowed = new Set(selectedAccountTypes);
      result = result.filter(t => {
        if (!t.accountId) return true;
        const type = accountTypeByIdMemo.get(t.accountId);
        return type ? allowed.has(type) : true;
      });
    }

    // Then filter by active accounts only if enabled (and "all" is selected)
    if (activeAccountsOnly && selectedAccountFilter === 'all') {
      result = result.filter(t => !t.accountId || activeAccountIds.includes(t.accountId));
    }

    // Filter out pre-reset trades if the toggle is off
    if (!includeResetTradesInStats) {
      result = result.filter(t => {
        if (!t.accountId) return true; // trades with no account always shown
        const account = getAccountById(t.accountId);
        if (!account) return true;
        const currentPeriod = account.currentPeriod || 1;
        return !t.period || t.period === currentPeriod;
      });
    }

    // Filter by time period — string compare against trade.date anchored at the
    // current trading day. Replaces the old `new Date(t.date) >= cutoffDate`
    // pattern, which UTC-shifted YYYY-MM-DD strings and dropped boundary days
    // for non-ET hosts. tradeDateInLastNDays is inclusive on both ends.
    if (timePeriodFilter !== 'all') {
      const days = parseInt(timePeriodFilter);
      if (Number.isFinite(days) && days > 0) {
        result = result.filter(t => tradeDateInLastNDays(t.date, days));
      }
    }

    return result;
  })();

  // Closed trades only — excludes in-progress (isOpen) trades for stats/analytics
  const closedFilteredTrades = filteredTrades.filter(t => !t.isOpen);

  // Clean trades: exclude mistake-tagged trades for setup/ATM analytics
  const cleanClosedTrades = useMemo(() =>
    closedFilteredTrades.filter(t => !t.mistakes || t.mistakes.length === 0),
    [closedFilteredTrades]
  );

  // Base trades for setup/ATM stat functions — switches based on Clean Metrics toggle
  const setupAtmBaseTrades = excludeMistakesFromSetupStats ? cleanClosedTrades : closedFilteredTrades;

  return {
    activeAccountIds,
    filteredTrades,
    closedFilteredTrades,
    cleanClosedTrades,
    setupAtmBaseTrades,
  };
};

export default useTradeFiltering;
